import PlayerBlockMemorized, { PlayerBlockProps } from '@anm/components/PlayerBlock';
import { DragItem } from '@anm/components/drag-drop';
import useSwitchState from '@anm/hooks/useSwitchState';
import { memo, useContext, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import vtConfig from '../../../config';
import { userSelectors } from '../../../store/modules/user';
import { videoFolderActions, videoFolderSelectors } from '../../../store/modules/videoFolder';
import { ProjectsDragDropPayload } from '../ProjectFolderBlock';
import AlreadyInsideAlert from '../ProjectPlayerBlock/AlreadyInsideAlert';
import DeleteProjectModal from '../ProjectPlayerBlock/DeleteProjectModal';
import { useDuplicateVideoAlert } from '../ProjectPlayerBlock/DuplicateVideoAlert';
import EditEmbeddedAlert from '../ProjectPlayerBlock/EditEmbeddedAlert';
import { ProjectFolderTreeModalContext } from '../ProjectPlayerBlock/ProjectFolderTreeModalProvider';
import { Wrapper } from '../ProjectPlayerBlock/Wrapper';
import areProjectPlayerBlockPropsEqual from '../ProjectPlayerBlock/helpers/areProjectPlayerBlockPropsEqual';
import getEditLink from '../ProjectPlayerBlock/helpers/getEditLink';
import useDuplicateHandlers from '../ProjectPlayerBlock/hooks/useDuplicateHandlers';
import useProjectPreview from '../ProjectPlayerBlock/hooks/useProjectPreview';
import useUpdatePreview from '../ProjectPlayerBlock/hooks/useUpdatePreview';

import useImageProjectDropdownActions from './hooks/useImageProjectDropdownActions';

export type ImageProjectProps = Pick<
  PlayerBlockProps,
  'title' | 'width' | 'height' | 'hasTag' | 'modified' | 'onRename'
> & {
  version: number;
  projectId: string;
  hostId?: string;
  shouldUpdatePreview?: boolean;
};

const { urls } = vtConfig();

const ImageProject: FC<ImageProjectProps> = ({
  title,
  width,
  height,
  hostId,
  hasTag,
  version,
  projectId,
  shouldUpdatePreview,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useSwitchState();
  const [isAlreadyInsideAlertOpen, openAlreadyInsideAlert, closeAlreadyInsideAlert] = useSwitchState();

  const [isFieldFocused, setFieldFocused] = useState(false);
  const [isEditEmbeddedAlertOpen, openEditEmbeddedModal, closeEditEmbeddedModal] = useSwitchState();

  const [duplicateAlert, openDuplicateAlert] = useDuplicateVideoAlert();
  const [handleDuplicate, handleDuplicateAndEdit] = useDuplicateHandlers({
    version,
    duration: 0,
    projectId,
    openDuplicateAlert
  });

  const isUserStaff = useSelector(userSelectors.selectIsStaffUser);
  const currentFolderId = useSelector(videoFolderSelectors.selectCurrentFolderId);

  const { openFolderTree } = useContext(ProjectFolderTreeModalContext);

  const { moveProject, updateProjectMeta } = videoFolderActions;

  const isDuplicatePending = useSelector(state => videoFolderSelectors.selectProjectDuplicateStatus(state, projectId));
  const isProjectPending = isDuplicatePending;

  const openFolderTreeWithProps = () =>
    !isAlreadyInsideAlertOpen &&
    openFolderTree({
      moveItemName: 'video',
      onMoveFolder: handleMoveProject
    });

  const preview = useProjectPreview(projectId);

  const onRename = (newName: string) => newName && dispatch(updateProjectMeta({ projectId, meta: { name: newName } }));

  const renderEditLink = getEditLink(true, projectId, openEditEmbeddedModal);

  const handleMoveProject = (folderId: number) => {
    return currentFolderId === folderId ? openAlreadyInsideAlert() : dispatch(moveProject({ folderId, projectId }));
  };

  const isProjectDraggable = !isFieldFocused;

  useUpdatePreview({
    isCreated: true,
    shouldUpdatePreview,
    id: projectId
  });

  const dropdownActions = useImageProjectDropdownActions({
    renderEditLink,
    onDelete: openDeleteModal,
    onDuplicate: handleDuplicate,
    onMove: openFolderTreeWithProps
  });

  const editorUrl = `${urls.editor}/#p=${projectId}`;
  return (
    <Wrapper>
      <div>
        {duplicateAlert}
        {isAlreadyInsideAlertOpen && <AlreadyInsideAlert {...{ projectId }} onClose={closeAlreadyInsideAlert} />}
        {isDeleteModalOpen && <DeleteProjectModal {...{ projectId }} onClose={closeDeleteModal} />}
        {isEditEmbeddedAlertOpen && (
          <EditEmbeddedAlert {...{ editorUrl }} onCancel={handleDuplicateAndEdit} onClose={closeEditEmbeddedModal} />
        )}

        <DragItem<ProjectsDragDropPayload>
          payload={{ projectId }}
          draggable={isProjectDraggable}
          dragPreview={preview || ''}
        >
          <PlayerBlockMemorized
            {...{
              title,
              width,
              height,
              hostId,
              preview,
              onRename,
              renderEditLink
            }}
            canStreamVideo={false}
            showProgress={false}
            hasTag={isUserStaff && hasTag}
            actions={dropdownActions}
            isPending={isProjectPending}
            uploadProgress={null}
            actionsDividerIndex={2}
            onFieldFocused={setFieldFocused}
            {...otherProps}
          />
        </DragItem>
      </div>
    </Wrapper>
  );
};

export const ImageProjectMemorized = memo(ImageProject, areProjectPlayerBlockPropsEqual);

const ProjectGuard: FC<ImageProjectProps> = props => {
  const { projectId } = props;

  const project = useSelector(state => videoFolderSelectors.selectProjectById(state, projectId));

  return <>{project && <ImageProjectMemorized {...props} />}</>;
};

export default ProjectGuard;
